import {useCallback, useEffect, useRef} from 'react';
import {useQueryClient} from '@tanstack/react-query';
import {addBookmark, removeBookmark} from '../endpoints/api';
import {ToastMessage} from '../components/toast';

// Debounce window for coalescing rapid taps on the same article. Long enough to absorb
// a back-to-back toggle (tap, then tap-again to undo) without round-tripping the server
// twice; short enough that the window for losing a request to a tab close stays small.
const DEBOUNCE_MS = 1500;

const ADD_URL: Record<string, (id: number) => string> = {
  event: (id) => `events/${id}/add`,
  offer: (id) => `offers/${id}/bookmark`,
  place: (id) => `places/${id}/bookmark`,
  'meet-up': (id) => `events/${id}/add`,
};

const REMOVE_URL: Record<string, (id: number) => string> = {
  event: (id) => `events/${id}/remove`,
  offer: (id) => `offers/${id}/remove-bookmark`,
  place: (id) => `places/${id}/remove-bookmark`,
  'meet-up': (id) => `events/${id}/remove`,
};

interface PendingEntry {
  desired: boolean;
  type: string;
  timer: ReturnType<typeof setTimeout> | null;
  // Optional follow-on side effect (e.g. auto-RSVP an event as Interested) that should
  // run only if the bookmark POST itself actually executes. Coalesced no-op fires skip
  // it, so a tap+undo within the debounce window can't leave behind orphan RSVPs.
  onSuccess?: () => void | Promise<unknown>;
}

interface EnqueueArgs {
  articleId: number;
  type: string;
  desired: boolean;
  autoRsvp?: () => void | Promise<unknown>;
}

/**
 * Per-article debounce/coalesce queue for bookmark mutations.
 *
 * Why: the BookmarkAhead feed has no backend caching, so a single bookmark used to
 * trigger a full feed recompute (~all four sections, runtime). Optimistic UI handles
 * the visible state; this queue keeps the network from chasing every tap.
 *
 * Coalesce semantics: only the latest desired state per articleId is sent. Tap
 * bookmark→unbookmark→bookmark within the window collapses to a single bookmark POST.
 *
 * Failure handling: on the rare API error we invalidate the feed query so the server
 * response replaces all optimistic deltas. This is the one place we accept the feed
 * refetch cost — it's the cheapest way to keep optimistic state from drifting after
 * an error without bookkeeping every per-tap rollback.
 *
 * Flush points: hook unmount and `pagehide` (the latter is the iOS-Safari-friendly
 * counterpart to `beforeunload`). Axios doesn't support `fetch` `keepalive`, so a tap
 * immediately followed by a tab close has a small window where the request can be
 * aborted by the browser. Acceptable: next page mount triggers a feed fetch which
 * reconciles to server truth.
 */
export function useBookmarkQueue() {
  const queryClient = useQueryClient();
  const queueRef = useRef<Map<number, PendingEntry>>(new Map());

  const fire = useCallback(
    async (articleId: number) => {
      const entry = queueRef.current.get(articleId);
      if (!entry) return;
      queueRef.current.delete(articleId);
      const url = entry.desired ? ADD_URL[entry.type] : REMOVE_URL[entry.type];
      if (!url) return;
      try {
        if (entry.desired) {
          await addBookmark({articleId, customUrl: url});
        } else {
          await removeBookmark({articleId, customUrl: url});
        }
        if (entry.onSuccess) {
          await entry.onSuccess();
        }
      } catch {
        queryClient.invalidateQueries(['bookmark-ahead-feed']);
        ToastMessage({
          status: 'error',
          text: 'Failed to update bookmark. Please try again.',
        });
      }
    },
    [queryClient],
  );

  const enqueue = useCallback(
    ({articleId, type, desired, autoRsvp}: EnqueueArgs) => {
      const existing = queueRef.current.get(articleId);
      if (existing?.timer) clearTimeout(existing.timer);
      const timer = setTimeout(() => fire(articleId), DEBOUNCE_MS);
      // Latest tap wins for desired state; a later tap that flips back to the original
      // server state with no autoRsvp will overwrite an earlier entry that had one,
      // which is the correct behavior (the bookmark — and therefore the RSVP — was
      // cancelled before either reached the server).
      queueRef.current.set(articleId, {desired, type, timer, onSuccess: autoRsvp});
    },
    [fire],
  );

  const flushAll = useCallback(() => {
    queueRef.current.forEach((entry, articleId) => {
      if (entry.timer) clearTimeout(entry.timer);
      void fire(articleId);
    });
  }, [fire]);

  useEffect(() => {
    const onPageHide = () => flushAll();
    window.addEventListener('pagehide', onPageHide);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      flushAll();
    };
  }, [flushAll]);

  return {enqueue};
}
